import { inject, makeStateKey, NgZone, TransferState } from '@angular/core';
import { Environment, IS_PRODUCTION } from '@rma/generic/util/environment';
import { basicLogger, initialize, type LDClient, LDOptions } from 'launchdarkly-js-client-sdk';
import { LDFlagSet, LDFlagValue, LDMultiKindContext, LDSingleKindContext } from 'launchdarkly-js-sdk-common';
import { fromEventPattern, Observable, of } from 'rxjs';
import { startWith, take } from 'rxjs/operators';
import { anonymousUser } from '../domain/models/anonymous-user.constant';
import { FeatureFlag } from '../domain/models/feature-flags.enum';
import { suppressLdErrors } from '../util-launch-darkly-errors/launch-darkly-errors';
import { FeatureFlagService } from './feature-flag.abstract.service';

export class FeatureFlagsBrowserService implements FeatureFlagService {
  private readonly environment = inject(Environment);
  private readonly state = inject(TransferState, { optional: true });
  private readonly zone = inject(NgZone);

  private readonly featureSetKey = makeStateKey<LDFlagSet>('ld_flags');

  private ldClient?: LDClient;

  private featureWatchers: Partial<Record<FeatureFlag, Observable<LDFlagValue>>> = {};

  public load(): Promise<void> {
    return new Promise((resolve, _reject) => this.zone.runOutsideAngular(() => this.initialiseClient(resolve)));
  }

  public getFeature<T = boolean>(key: FeatureFlag, defaultValue: T): T {
    return this.ldClient?.variation(key, defaultValue) ?? defaultValue;
  }

  public getFeature$<T = boolean>(key: FeatureFlag, defaultValue: T): Observable<T> {
    return this.getLiveFeature$(key, defaultValue).pipe(take(1));
  }

  public getLiveFeature$<T = boolean>(key: FeatureFlag, defaultValue: T): Observable<T> {
    if (this.featureWatchers[key]) {
      return this.featureWatchers[key];
    }

    const initialValue: T = this.ldClient?.variation(key, defaultValue) ?? defaultValue;

    const feature$ = this.ldClient
      ? fromEventPattern<T>((handler) => this.ldClient?.on?.(`change:${key}`, handler)).pipe(startWith(initialValue))
      : of(initialValue);

    this.featureWatchers[key] = feature$;

    return feature$;
  }

  public async identify(user: LDMultiKindContext | LDSingleKindContext): Promise<void> {
    if (this.ldClient) {
      await this.ldClient.identify(user);
    }
  }

  private initialiseClient(resolve: (value: void | PromiseLike<void>) => void) {
    const flags = this.state?.get(this.featureSetKey, undefined);
    const bootstrapConfig = flags && Object.values(flags).length ? { bootstrap: flags } : {};

    const config: LDOptions = {
      ...bootstrapConfig,
      logger: basicLogger({ level: IS_PRODUCTION ? 'error' : 'warn' }),
      sendEvents: true,
    };

    this.ldClient = initialize(this.environment.launchDarklyClientId, anonymousUser, config);

    this.ldClient.on('ready', () => resolve());

    this.ldClient.on('error', suppressLdErrors);
  }
}
